// src/pages/PermissionManagementPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "@/api/apiCall";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Settings2 } from "lucide-react";
import DataTable from "@/components/common/DataTable";
import useDebounce from "@/hooks/useDebounce";
import { formatDateTime } from "@/lib/formatDateTime";
import { usePermissions } from "@/context/PermissionContext";

const PermissionManagementPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // ── State ──────────────────────────────────────────────────────────────────
  const [permissions, setPermissions] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, perPage: 20, totalRecords: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const hasMounted = useRef(false);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchPermissions = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);
      let url = `/permissions/get-list?page=${page}&perPage=${perPage}`;
      if (sortBy && sortOrder) url += `&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      if (debouncedSearch.trim()) url += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
      const res = await apiCall("GET", url, null, navigate);
      if (res?.success) {
        const data = res.data || [];
        setPermissions(data);
        if (res.pagination) {
          setPagination(res.pagination);
        } else {
          setPagination((p) => ({ ...p, currentPage: page, perPage, totalRecords: data.length, totalPages: 1 }));
        }
      }
    } catch (err) { console.error("Failed to load permissions:", err); }
    finally { setLoading(false); }
  };

  // useEffect(() => {
  //   if (hasMounted.current) return;
  //   hasMounted.current = true;
  //   fetchPermissions(1);
  // }, []);

  // useEffect(() => {
  //   if (!hasMounted.current) return;
  //  fetchPermissions(1);
  // }, [debouncedSearch, sortBy, sortOrder]);

  useEffect(() => {
      if (hasMounted.current) return;
    hasMounted.current = true;
  fetchPermissions(1);
}, [debouncedSearch, sortBy, sortOrder]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortBy === col) setSortOrder((p) => (p === "ASC" ? "DESC" : "ASC"));
    else { setSortBy(col); setSortOrder("ASC"); }
  };

  // ── DataTable config ───────────────────────────────────────────────────────
  const columns = [
    {
      key: "permission_key",
      label: "Permission Key",
      sortable: false,
      render: (row) => (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-secondary text-secondary-foreground">
          {row.permission_key}
        </span>
      ),
    },
    {
      key: "module_name",
      label: "Module",
      sortable: false,
      render: (row) => <span className="font-mono text-sm">{row.module_name}</span>,
    },
    {
      key: "description",
      label: "Description",
      render: (row) => <span className="text-muted-foreground text-sm">{row.description || "—"}</span>,
    },
    {
      key: "is_active",
      label: "Status",
      sortable: false,
      render: (row) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${row.is_active ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"}`}>
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "created_datetime",
      label: "Created At",
      sortable: false,
      render: (row) => (
        <span className="text-muted-foreground text-sm">
          {row.created_datetime ? formatDateTime(row.created_datetime) : "—"}
        </span>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <DataTable
      title="Permission Management"
      description="View and assign permissions to roles"
      // icon={<ShieldCheck className="h-5 w-5" />}
      headerActions={
        hasPermission("permissions_assign") && (
          <Button onClick={() => navigate("/assign-permission")} className="gap-2 w-full sm:w-auto h-9 text-sm">
            <Settings2 className="h-4 w-4" /> Assign Permission
          </Button>
        )
      }
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search permissions..."
      columns={columns}
      rows={permissions}
      rowKey="permission_id"
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
      pagination={pagination}
      onPageChange={(p) => fetchPermissions(p, pagination.perPage)}
      onPerPageChange={(pp) => fetchPermissions(1, Number(pp))}
      paginationTitle="permissions"
      showPagination={false}
      showSearch={false}
      loading={loading}
      emptyMessage="No permissions found."
    />
  );
};

export default PermissionManagementPage;